<template>
  <div class="tab-manager">
    <!-- Tab Bar Container -->
    <div class="tab-bar-wrapper">
      <!-- Scroll left button -->
      <v-btn
        v-show="showScrollButtons && canScrollLeft"
        icon
        small
        @click="scrollTabs('left')"
        class="tab-scroll-btn"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-tabs
        v-model="activeTabIndex"
        ref="tabsContainer"
        background-color="transparent"
        slider-color="primary"
        class="tabs-bar"
        style="flex: 1 1 auto !important; min-width: 0;"
      >
        <!-- Osnovni pristup sa indeksima -->
        <v-tab
          v-for="(tab, index) in tabs"
          :key="tab.id"
          :value="index"
          :ref="`tab_${tab.id}`"
          @dblclick="enableRename(tab)"
          class="tab-item"
        >
        <!-- Tooltip za prikaz datuma kreiranja -->
        <v-tooltip bottom v-if="tab.createdAt && renamingTabId !== tab.id">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="tab-title">
              {{ tab.title }}
            </span>
          </template>
          <span>Kreirano: {{ tab.createdAt }}</span>
        </v-tooltip>

        <!-- Prikaz naziva bez tooltipa (za fiksne tabove ili ako nema createdAt) -->
        <span v-else-if="renamingTabId !== tab.id" class="tab-title">
          {{ tab.title }}
        </span>

        <input
          v-else
          v-model="tempTabName"
          @blur="finishRename(tab.id)"
          @keyup.enter="finishRename(tab.id)"
          @keyup.esc="cancelRename"
          :ref="`tabInput_${tab.id}`"
          class="tab-input"
          @click.stop
        />

        <!-- Close button -->
        <v-btn
          v-if="tab.closable"
          icon
          x-small
          @click.stop="handleCloseTab(tab.id)"
          class="ml-2"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-tab>
    </v-tabs>

      <!-- Scroll right button -->
      <v-btn
        v-show="showScrollButtons && canScrollRight"
        icon
        small
        @click="scrollTabs('right')"
        class="tab-scroll-btn"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Tab Content - koristi v-tabs-items sa indeksima -->
    <v-tabs-items v-model="activeTabIndex" class="tab-content">
      <!-- Svi tabovi -->
      <v-tab-item
        v-for="tab in tabs"
        :key="tab.id"
        :eager="true"
      >
        <!-- Receipts content -->
        <Receipts
          v-if="tab.type === 'receipts' || tab.type === 'offers'"
          :ref="`receipts_${tab.id}`"
          :receiptFilter="tab.props.receiptFilter"
        />

        <!-- CRUD content -->
        <div v-else-if="tab.type === 'crud'" class="crud-tab-content" style="height: 100%; width: 100%;">
          <crud-template-dialog
            :key="tab.id"
            :ref="`dialog_${tab.id}`"
            :tab-mode="true"
            @receipt-issued="onReceiptIssued(tab.id, $event)"
            @state-changed="onCrudStateChanged(tab.id, $event)"
          />
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import Receipts from '@/modules/company/components/receipts/Receipts'
import CrudTemplateDialog from '@/modules/cash-register/components/dialogs/CrudTemplateDialog'
import state from '@/state'

export default {
  name: 'TabManager',
  inject: ['confirm', 'confirmClose'],
  components: {
    Receipts,
    CrudTemplateDialog
  },
  props: {
    lastBalanceTimestamp: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      tabs: [],
      activeTabIndex: 0,
      visibleTabCount: 0,
      containerWidth: 0,
      overflowButtonWidth: 80,
      renamingTabId: null,
      tempTabName: '',
      programmaticChange: false,
      showScrollButtons: false,
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  computed: {
    visibleTabs () {
      return this.tabs.slice(0, this.visibleTabCount)
    },
    overflowTabs () {
      return this.tabs.slice(this.visibleTabCount)
    },
    receiptsTabs () {
      return this.tabs.filter(t => t.type === 'receipts' || t.type === 'offers')
    },
    crudTabs () {
      return this.tabs.filter(t => t.type === 'crud')
    }
  },
  watch: {
    // activeTabIndex watcher više nije potreban jer koristimo activeTabId direktno
    tabs: {
      handler () {
        this.saveToLocalStorage()
      },
      deep: true
    },
    activeTabIndex (newIndex, oldIndex) {
      this.saveToLocalStorage()
    }
  },
  mounted () {
    // Pokušaj učitati tabove iz local storage
    const loaded = this.loadFromLocalStorage()

    if (!loaded) {
      // Nema spremljenog stanja, inicijaliziraj sa defaultnim tabom
      this.ensureTabsInitialized()
      this.calculateVisibleTabs()
    } else {
      // Spremljeno stanje učitano
      this.$nextTick(() => {
        // Prvo izračunaj vidljive tabove
        this.calculateVisibleTabs()

        // Zatim prebaci na aktivni tab (ako postoji)
        this.$nextTick(() => {
          if (this.activeTabId) {
            this.switchTab(this.activeTabId)
          }

          // Otvori sve CRUD tabove sa njihovim spremljenim stanjem
          // Daj više vremena da se Vue komponente renderiraju
          setTimeout(() => {
            this.crudTabs.forEach(tab => {
              const dialogRef = this.$refs[`dialog_${tab.id}`]
              if (dialogRef && dialogRef[0]) {
                dialogRef[0].open(true, tab.props.register, tab.crudState)
              }
            })
          }, 100)
        })
      })
    }

    window.addEventListener('resize', () => {
      this.calculateVisibleTabs()
      this.updateScrollButtonsVisibility()
    })

    // Postavi inicijalnu vidljivost scroll gumba
    this.$nextTick(() => {
      this.updateScrollButtonsVisibility()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', () => {
      this.calculateVisibleTabs()
      this.updateScrollButtonsVisibility()
    })
  },
  methods: {
    // Generiraj ključ za local storage
    getStorageKey () {
      const companyId = state.getCurrentCompany().id
      const registerId = state.getCashRegister().id
      return `cash-register-tabs-${companyId}-${registerId}`
    },

    // Spremi tabove u local storage
    saveToLocalStorage () {
      try {
        // Fiksni tabovi koje ne želimo spremati
        const fixedTabIds = ['receipts-main', 'all-receipts-main', 'offers-main']

        // Filtriraj samo CRUD tabove i dohvati njihov state
        const tabsToSave = this.tabs
          .filter(tab => !fixedTabIds.includes(tab.id)) // Isključi fiksne tabove
          .map(tab => {
            if (tab.type === 'crud') {
              const dialogRef = this.$refs[`dialog_${tab.id}`]
              const crudState = dialogRef?.[0]?.getState?.() || null
              return { ...tab, crudState }
            }
            return tab
          })

        // Save active tab ID (not index) for compatibility
        const activeTab = this.tabs[this.activeTabIndex]
        const data = {
          tabs: tabsToSave,
          activeTabId: activeTab && !fixedTabIds.includes(activeTab.id) ? activeTab.id : null
        }

        localStorage.setItem(this.getStorageKey(), JSON.stringify(data))
      } catch (error) {
        console.error('Failed to save tabs to local storage:', error)
      }
    },

    // Učitaj tabove iz local storage
    loadFromLocalStorage () {
      try {
        const key = this.getStorageKey()
        const saved = localStorage.getItem(key)
        if (!saved) {
          return false
        }

        const data = JSON.parse(saved)

        // Ako nema spremljenih tabova, vrati false
        if (!data.tabs || data.tabs.length === 0) {
          return false
        }

        // Dodaj "Računi od zaključka" tab na početak samo ako ima CRUD tabova
        const receiptsTab = {
          id: 'receipts-main',
          title: 'Računi od zaključka',
          type: 'receipts',
          closable: true,
          editable: false,
          isPrimary: true,
          isFixed: true,
          component: 'Receipts',
          props: {
            receiptFilter: {
              oibFilter: undefined,
              reciptOffer: 'receipts',
              modal: true,
              registerId: state.getCashRegister().id,
              lastBalanceTimestamp: state.getCashRegister().last_balance_timestamp,
              hideFilter: true
            }
          }
        }

        this.tabs = [receiptsTab, ...data.tabs]
        // Find index of saved active tab
        if (data.activeTabId) {
          const index = this.tabs.findIndex(t => t.id === data.activeTabId)
          this.activeTabIndex = index !== -1 ? index : 0
        } else {
          this.activeTabIndex = 0
        }

        return true
      } catch (error) {
        console.error('Failed to load tabs from local storage:', error)
        return false
      }
    },

    // Ukloni CRUD tab iz local storage
    removeCrudTabFromStorage (tabId) {
      try {
        const key = this.getStorageKey()
        const saved = localStorage.getItem(key)
        if (!saved) return

        const data = JSON.parse(saved)
        data.tabs = data.tabs.filter(t => t.id !== tabId)
        localStorage.setItem(key, JSON.stringify(data))
      } catch (error) {
        console.error('Failed to remove tab from local storage:', error)
      }
    },

    // Inicijalizacija tabova - kreira "Računi od zaključka" tab
    ensureTabsInitialized () {
      if (this.tabs.length === 0) {
        const receiptsTab = {
          id: 'receipts-main',
          title: 'Računi od zaključka',
          type: 'receipts',
          closable: true,
          editable: false,
          isPrimary: true,
          isFixed: true, // Označi kao fiksan tab
          component: 'Receipts',
          props: {
            receiptFilter: {
              oibFilter: undefined,
              reciptOffer: 'receipts',
              modal: true,
              registerId: state.getCashRegister().id,
              lastBalanceTimestamp: state.getCashRegister().last_balance_timestamp,
              hideFilter: true
            }
          }
        }
        this.tabs.push(receiptsTab)
        this.activeTabIndex = 0
        this.calculateVisibleTabs()
      }
    },

    // Dodaj novi tab za račun
    async addNewReceiptTab (register, location) {
      this.ensureTabsInitialized()

      const now = new Date()
      const formattedDate = this.formatDateTime(now)

      // Izračunaj broj računa (koliko CRUD tabova već postoji + 1)
      const crudTabCount = this.tabs.filter(t => t.type === 'crud').length + 1

      const newTab = {
        id: uuidv4(),
        title: `Račun #${crudTabCount}`,
        createdAt: formattedDate, // Čuva datum za tooltip
        type: 'crud',
        closable: true,
        editable: true,
        component: 'CrudTemplateDialog',
        data: null,
        props: {
          register: register,
          location: location
        }
      }

      // Ubaci novi tab NA KRAJ - tako neće pomicati postojeće tabove
      this.tabs.push(newTab)

      // Čekaj da Vue ažurira tabs array u DOM-u
      await this.$nextTick()

      // Izračunaj vidljive tabove i čekaj da se završi
      await this.calculateVisibleTabs()

      // Još jedan nextTick da osiguramo da je DOM potpuno ažuriran
      await this.$nextTick()

      // switchTab će automatski riješiti sve - pozicioniranje, overflow, itd.
      this.switchTab(newTab.id)

      // Scrolla na novi tab da bude vidljiv
      await this.$nextTick()
      this.scrollTabIntoView(newTab.id)

      // Otvori dialog u tab mode-u
      await this.$nextTick()
      const dialogRef = this.$refs[`dialog_${newTab.id}`]
      if (dialogRef && dialogRef[0]) {
        // Proslijedi crudState ako postoji (iz local storage)
        dialogRef[0].open(true, register, newTab.crudState)
      }
    },

    // Otvori ili prebaci na "Računi" tab (svi računi, ne samo od zaključka)
    async openReceiptsTab () {
      this.ensureTabsInitialized()

      const receiptsTab = this.tabs.find(t => t.id === 'all-receipts-main')

      if (receiptsTab) {
        // Tab već postoji, samo prebaci na njega
        const tabIndex = this.tabs.findIndex(t => t.id === 'all-receipts-main')
        if (tabIndex !== -1) {
          this.activeTabIndex = tabIndex
        }
        await this.calculateVisibleTabs()
        await this.$nextTick()
        this.scrollTabIntoView('all-receipts-main')
      } else {
        const newTab = {
          id: 'all-receipts-main',
          title: 'Računi i Ponude',
          type: 'receipts',
          closable: true,
          editable: false,
          isFixed: true, // Označi kao fiksan tab
          component: 'Receipts',
          props: {
            receiptFilter: {
              reciptOffer: 'receipts',
              modal: false,
              lastBalanceTimestamp: undefined, // Ne filterira po zaključku
              hideFilter: false
            }
          }
        }

        // Dodaj nakon "Računi od zaključka" taba (uvijek na poziciju 1)
        this.tabs.splice(1, 0, newTab)

        // Čekaj da Vue ažurira DOM prije prebacivanja
        await this.$nextTick()

        // Direktno postavi activeTabIndex umjesto switchTab da izbjegnemo Vuetify's auto-adjustment
        this.activeTabIndex = 1

        await this.calculateVisibleTabs()
        await this.$nextTick()
        this.scrollTabIntoView('all-receipts-main')
      }
    },

    // Otvori ili prebaci na "Ponude" tab
    async openOffersTab () {
      this.ensureTabsInitialized()

      const offersTab = this.tabs.find(t => t.id === 'offers-main')

      if (offersTab) {
        // Tab već postoji, samo prebaci na njega
        const tabIndex = this.tabs.findIndex(t => t.id === 'offers-main')
        if (tabIndex !== -1) {
          this.activeTabIndex = tabIndex
        }
        await this.calculateVisibleTabs()
        await this.$nextTick()
        this.scrollTabIntoView('offers-main')
      } else {
        const newTab = {
          id: 'offers-main',
          title: 'Ponude',
          type: 'offers',
          closable: true,
          editable: false,
          isFixed: true, // Označi kao fiksan tab
          component: 'Receipts',
          props: {
            receiptFilter: {
              reciptOffer: 'offers',
              modal: false,
              lastBalanceTimestamp: this.lastBalanceTimestamp,
              hideFilter: false
            }
          }
        }

        // Dodaj nakon "Računi" ili "Računi od zaključka" taba
        // Provjeri postojanje all-receipts-main taba
        const receiptsTabIndex = this.tabs.findIndex(t => t.id === 'all-receipts-main')
        const insertPosition = receiptsTabIndex >= 0 ? receiptsTabIndex + 1 : 1

        this.tabs.splice(insertPosition, 0, newTab)

        // Čekaj da Vue ažurira DOM prije prebacivanja
        await this.$nextTick()

        // Direktno postavi activeTabIndex umjesto switchTab da izbjegnemo Vuetify's auto-adjustment
        this.activeTabIndex = insertPosition

        await this.calculateVisibleTabs()
        await this.$nextTick()
        this.scrollTabIntoView('offers-main')
      }
    },

    // Prebaci na drugi tab
    switchTab (newTabId) {
      // Pronađi indeks taba sa tim ID-em
      const index = this.tabs.findIndex(t => t.id === newTabId)
      if (index !== -1) {
        this.activeTabIndex = index
      }
    },

    // Handler za zatvaranje taba s provjerom nesačuvanih podataka
    async handleCloseTab (tabId) {
      const tabIndex = this.tabs.findIndex(t => t.id === tabId)
      const tab = this.tabs[tabIndex]

      if (!tab.closable) return

      // Provjeri ima li nesačuvanih podataka u CRUD tabu
      if (tab.type === 'crud') {
        const dialogRef = this.$refs[`dialog_${tabId}`]
        if (dialogRef && dialogRef[0]) {
          // Provjeri ima li nešto u košarici
          const hasData = dialogRef[0].cart && dialogRef[0].cart.length > 0 && dialogRef[0].cart[0]?.data?.name

          if (hasData) {
            // Prikaži upozorenje koristeći confirm
            const confirmed = await this.confirm({
              title: 'Zatvaranje taba',
              message: 'Imate nespremljene podatke u ovom tabu. Jeste li sigurni da želite zatvoriti tab? Svi uneseni podaci će biti izgubljeni.',
              options: {
                toolbar: true,
                width: 450,
                confirmText: 'Zatvori tab',
                cancelText: 'Odustani'
              }
            })

            if (!confirmed) {
              this.confirmClose()
              return
            }

            // Zatvori confirm dialog
            this.confirmClose()
          }
        }
      }

      // Zatvori tab
      this.closeTab(tabId, true)
    },

    // Zatvori tab (interno)
    closeTab (tabId, force = false) {
      const tabIndex = this.tabs.findIndex(t => t.id === tabId)
      const tab = this.tabs[tabIndex]

      if (!tab.closable) return

      // Ukloni iz local storage ako je CRUD tab
      if (tab && tab.type === 'crud') {
        this.removeCrudTabFromStorage(tabId)
      }

      // Makni tab
      this.tabs.splice(tabIndex, 1)

      // Ako je to bio aktivni tab, prebaci na prvi tab
      if (this.activeTabIndex === tabIndex) {
        if (this.tabs.length > 0) {
          // Ako je zadnji tab zatvoren, idi na prethodni
          this.activeTabIndex = Math.min(tabIndex, this.tabs.length - 1)
        } else {
          this.activeTabIndex = 0
        }
      } else if (this.activeTabIndex > tabIndex) {
        // Ako je zatvoren tab prije aktivnog, smanji indeks
        this.activeTabIndex--
      }

      // Ako ostane samo "Računi od zaključka" tab, emituj event da se vrati na inicijalno stanje
      if (this.tabs.length === 1 && this.tabs[0].id === 'receipts-main') {
        this.tabs = []
        this.activeTabIndex = 0
        this.$emit('tabs-empty')
      }

      this.$nextTick(() => {
        this.calculateVisibleTabs()

        // Scrolla na aktivni tab nakon brisanja
        this.$nextTick(() => {
          if (this.tabs.length > 0 && this.tabs[this.activeTabIndex]) {
            this.scrollTabIntoView(this.tabs[this.activeTabIndex].id)
          }
        })
      })
    },

    // Callback nakon izdavanja računa
    onReceiptIssued (tabId, receiptData) {
      // Ukloni iz local storage
      this.removeCrudTabFromStorage(tabId)

      // Zatvori tab računa
      this.closeTab(tabId, true)

      // Prebaci na "Računi" tab
      this.switchTab('receipts-main')

      // Refresh liste računa
      const receiptsRef = this.$refs['receipts_receipts-main']
      if (receiptsRef && receiptsRef[0] && receiptsRef[0].refresh) {
        receiptsRef[0].refresh()
      } else if (receiptsRef && receiptsRef[0] && receiptsRef[0].getReceiptsOrOffers) {
        receiptsRef[0].getReceiptsOrOffers()
      }
    },

    // Handler za promjenu stanja CRUD taba
    onCrudStateChanged (tabId, state) {
      const tab = this.tabs.find(t => t.id === tabId)
      if (tab) {
        tab.crudState = state
        // saveToLocalStorage će biti pozvan automatski preko watchera
      }
    },

    // Enable rename za tab
    enableRename (tab) {
      if (!tab.editable) return

      this.renamingTabId = tab.id
      this.tempTabName = tab.title

      this.$nextTick(() => {
        const inputRef = this.$refs[`tabInput_${tab.id}`]
        if (inputRef && inputRef[0]) {
          inputRef[0].focus()
          inputRef[0].select()
        }
      })
    },

    // Završi rename
    finishRename (tabId) {
      const tab = this.tabs.find(t => t.id === tabId)
      if (this.tempTabName.trim()) {
        tab.title = this.tempTabName.trim()
      }
      this.renamingTabId = null
      this.tempTabName = ''
    },

    // Odustani od rename
    cancelRename () {
      this.renamingTabId = null
      this.tempTabName = ''
    },

    // Dovedi tab iz overflow-a na prednju poziciju
    async bringTabToFront (tabId) {
      const tabIndex = this.tabs.findIndex(t => t.id === tabId)
      if (tabIndex >= this.visibleTabCount) {
        // Tab je u overflow-u, trebamo ga dovesti naprijed
        const tab = this.tabs.splice(tabIndex, 1)[0]

        // Pronađi prvu poziciju nakon fiksnih tabova (receipts-main, all-receipts-main, offers-main)
        const fixedTabIds = ['receipts-main', 'all-receipts-main', 'offers-main']
        let insertPosition = 0

        for (let i = 0; i < this.tabs.length; i++) {
          if (fixedTabIds.includes(this.tabs[i].id)) {
            insertPosition = i + 1
          }
          // Ne radi break - nastavi da pronađe zadnji fiksni tab
        }

        this.tabs.splice(insertPosition, 0, tab)

        // Čekaj da se DOM ažurira
        await this.$nextTick()

        // Izračunaj vidljive tabove i čekaj da se završi
        await this.calculateVisibleTabs()

        // Još jedan nextTick za sigurnost
        await this.$nextTick()

        // Koristi switchTab funkciju koja pravilno postavlja i activeTabId i activeTabIndex
        this.switchTab(tabId)

        // Scrolla na tab nakon što je doveden naprijed
        await this.$nextTick()
        this.scrollTabIntoView(tabId)
      } else {
        this.switchTab(tabId)
        this.scrollTabIntoView(tabId)
      }
    },

    // Izračunaj koliko tabova stane u širinu
    calculateVisibleTabs () {
      return new Promise((resolve) => {
        this.$nextTick(() => {
          if (!this.$refs.tabsContainer || this.tabs.length === 0) {
            this.visibleTabCount = this.tabs.length
            resolve()
            return
          }

          const container = this.$refs.tabsContainer.$el
          this.containerWidth = container.offsetWidth

          let usedWidth = 0
          let count = 0
          let willHaveOverflow = false

          // Prvo probaj stati sve tabove bez overflow buttona
          for (let i = 0; i < this.tabs.length; i++) {
            const tabWidth = this.measureTabWidth(this.tabs[i])

            if (usedWidth + tabWidth <= this.containerWidth) {
              usedWidth += tabWidth
              count++
            } else {
              // Ne stanu svi tabovi, trebat će overflow button
              willHaveOverflow = true
              break
            }
          }

          // Ako će biti overflow, preračunaj sa rezerviranim prostorom za overflow button
          if (willHaveOverflow) {
            usedWidth = 0
            count = 0
            const availableWidth = this.containerWidth - this.overflowButtonWidth

            for (let i = 0; i < this.tabs.length; i++) {
              const tabWidth = this.measureTabWidth(this.tabs[i])

              if (usedWidth + tabWidth <= availableWidth) {
                usedWidth += tabWidth
                count++
              } else {
                break
              }
            }
          }

          // Minimalno 1 tab mora biti vidljiv (Računi tab)
          this.visibleTabCount = Math.max(1, count)

          // Ažuriraj vidljivost scroll gumba nakon što se tabovi preračunaju
          this.updateScrollButtonsVisibility()

          resolve()
        })
      })
    },

    // Izmjeri širinu taba
    measureTabWidth (tab) {
      // Pokušaj mjeriti stvarni DOM element ako postoji
      const tabRef = this.$refs[`tab_${tab.id}`]
      if (tabRef && tabRef[0] && tabRef[0].$el) {
        const actualWidth = tabRef[0].$el.offsetWidth
        if (actualWidth > 0) {
          return actualWidth
        }
      }

      // Fallback na procjenu ako ne možemo mjeriti
      const charWidth = 8
      const padding = 24
      const closeButtonWidth = tab.closable ? 24 : 0
      const margin = 8
      const iconPadding = 32

      return (tab.title.length * charWidth) + padding + closeButtonWidth + margin + iconPadding
    },

    // Format datuma i vremena
    formatDateTime (date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${day}.${month}.${year} ${hours}:${minutes}`
    },

    // Ažuriraj vidljivost scroll gumba
    updateScrollButtonsVisibility () {
      this.$nextTick(() => {
        if (!this.$refs.tabsContainer) {
          this.showScrollButtons = false
          return
        }

        const container = this.$refs.tabsContainer.$el
        const content = container.querySelector('.v-slide-group__content')
        const wrapper = container.querySelector('.v-slide-group__wrapper')

        if (!content || !wrapper) {
          this.showScrollButtons = false
          return
        }

        const contentWidth = content.scrollWidth
        const containerWidth = wrapper.clientWidth

        // Prikaži gumbe samo ako ima što scrollati
        this.showScrollButtons = contentWidth > containerWidth

        if (this.showScrollButtons) {
          // Dohvati trenutni transform
          const style = window.getComputedStyle(content)
          const matrix = new DOMMatrix(style.transform)
          const currentTranslateX = matrix.m41

          const maxScroll = 0
          const minScroll = -(contentWidth - containerWidth)

          // Možemo scrollati lijevo ako nismo na početku
          this.canScrollLeft = currentTranslateX < maxScroll - 10 // 10px tolerancija

          // Možemo scrollati desno ako nismo na kraju
          this.canScrollRight = currentTranslateX > minScroll + 10 // 10px tolerancija
        } else {
          this.canScrollLeft = false
          this.canScrollRight = false
        }
      })
    },

    // Scroll tabs left or right
    scrollTabs (direction) {
      if (!this.$refs.tabsContainer) {
        return
      }

      const container = this.$refs.tabsContainer.$el
      const content = container.querySelector('.v-slide-group__content')

      if (!content) {
        return
      }

      // Dohvati trenutni transform
      const style = window.getComputedStyle(content)
      const matrix = new DOMMatrix(style.transform)
      const currentTranslateX = matrix.m41 // m41 je translateX vrijednost

      // Izračunaj koliko možemo scrollati
      const contentWidth = content.scrollWidth
      const containerWidth = content.parentElement.clientWidth
      const maxTranslate = 0
      const minTranslate = -(contentWidth - containerWidth)

      const scrollAmount = 200
      let newTranslateX

      if (direction === 'left') {
        // Scroll lijevo = povećaj translateX (prema 0)
        newTranslateX = Math.min(maxTranslate, currentTranslateX + scrollAmount)
      } else {
        // Scroll desno = smanji translateX (prema negativnoj vrijednosti)
        newTranslateX = Math.max(minTranslate, currentTranslateX - scrollAmount)
      }

      // Postavi novi transform sa smooth transition
      content.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      content.style.transform = `translateX(${newTranslateX}px)`

      // Ukloni transition nakon animacije
      setTimeout(() => {
        content.style.transition = ''
        // Ažuriraj vidljivost gumba nakon scroll-a
        this.updateScrollButtonsVisibility()
      }, 300)
    },

    // Scrolla specifičan tab da bude vidljiv
    scrollTabIntoView (tabId) {
      if (!this.$refs.tabsContainer) {
        return
      }

      this.$nextTick(() => {
        const container = this.$refs.tabsContainer.$el
        const content = container.querySelector('.v-slide-group__content')
        const wrapper = container.querySelector('.v-slide-group__wrapper')

        if (!content || !wrapper) {
          return
        }

        // Pronađi tab element
        const tabRef = this.$refs[`tab_${tabId}`]
        if (!tabRef || !tabRef[0] || !tabRef[0].$el) {
          return
        }

        const tabElement = tabRef[0].$el

        // Dohvati pozicije
        const tabLeft = tabElement.offsetLeft
        const tabWidth = tabElement.offsetWidth
        const containerWidth = wrapper.clientWidth

        // Dohvati trenutni transform
        const style = window.getComputedStyle(content)
        const matrix = new DOMMatrix(style.transform)
        const currentTranslateX = matrix.m41

        // Izračunaj potreban translateX da tab bude vidljiv
        // Tab treba biti vidljiv sa malo margine (50px)
        const margin = 50
        const tabRight = tabLeft + tabWidth

        // Izračunaj vidljivi range
        const visibleLeft = -currentTranslateX
        const visibleRight = visibleLeft + containerWidth

        let newTranslateX = currentTranslateX

        // Ako je tab desno od vidljivog područja
        if (tabRight > visibleRight - margin) {
          newTranslateX = -(tabRight - containerWidth + margin)
        } else if (tabLeft < visibleLeft + margin) {
          // Ako je tab lijevo od vidljivog područja
          newTranslateX = -(tabLeft - margin)
        }

        // Ograniči na min/max
        const contentWidth = content.scrollWidth
        const maxTranslate = 0
        const minTranslate = -(contentWidth - containerWidth)
        newTranslateX = Math.max(minTranslate, Math.min(maxTranslate, newTranslateX))

        // Postavi transform sa animacijom
        content.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        content.style.transform = `translateX(${newTranslateX}px)`

        setTimeout(() => {
          content.style.transition = ''
          // Ažuriraj vidljivost gumba nakon scroll-a
          this.updateScrollButtonsVisibility()
        }, 300)
      })
    }
  }
}
</script>

<style scoped>
.tab-manager {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.tabs-bar {
  flex: none !important;
  flex-shrink: 0;
  background: linear-gradient(180deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(33, 150, 243, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Override Vuetify default flex - VAŽNO: maknuti flex: 1 1 auto */
.tab-manager >>> .v-tabs,
.tab-manager >>> .tabs-bar,
.tab-manager >>> .v-tabs.tabs-bar {
  flex: none !important;
}

/* Tab bar wrapper - container za v-tabs i overflow button */
.tab-bar-wrapper {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
}

/* Scroll buttons styling */
.tab-scroll-btn {
  flex: 0 0 auto;
  min-width: 40px !important;
  height: 48px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px 8px 0 0 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tab-scroll-btn:hover {
  background: rgba(33, 150, 243, 0.1) !important;
  color: #2196f3 !important;
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.15);
}

.tab-scroll-btn .v-icon {
  font-size: 20px;
}

.tab-manager >>> .v-tabs-bar {
  flex: 1 1 auto !important;
  width: 100% !important;
  display: flex !important;
}

.tab-manager >>> .v-tabs__wrapper {
  flex: 1 1 auto !important;
  width: 100% !important;
  display: flex !important;
}

.tab-manager >>> .v-slide-group__wrapper {
  flex: 1 1 auto !important;
  width: 100% !important;
  display: flex !important;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}

/* Sakrij scrollbar - koristimo custom gumbe */
.tab-manager >>> .v-slide-group__wrapper::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.tab-manager >>> .v-slide-group__wrapper {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.tab-manager >>> .v-slide-group__content {
  display: flex !important;
  width: 100% !important;
  flex-wrap: nowrap !important;
}

/* Tab styling with glass-morphism */
.tab-manager >>> .v-tab {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.3px;
  border-radius: 8px 8px 0 0;
  margin: 0 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(0, 0, 0, 0.6);
  flex-shrink: 0 !important; /* Sprječava sužavanje tabova */
  min-width: max-content !important; /* Održava prirodnu širinu */
}

/* Tabovi imaju normalan order */
.tab-manager >>> .v-tab.tab-item {
  order: 1 !important;
  flex: 0 0 auto !important; /* Ne dozvoli sužavanje */
  width: auto !important;
}

/* Overflow button - izvan v-tabs */
.overflow-button {
  min-width: 60px !important;
  height: 48px !important;
  border-radius: 8px 8px 0 0 !important;
  margin-left: 4px !important;
  color: rgba(0, 0, 0, 0.6) !important;
  background: rgba(255, 255, 255, 0.8) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.overflow-button:hover {
  background: rgba(33, 150, 243, 0.1) !important;
  color: #2196f3 !important;
}

.overflow-menu-wrapper {
  flex: 0 0 auto;
}

.tab-manager >>> .v-tab:hover:not(.v-tab--active) {
  background: rgba(33, 150, 243, 0.05);
  color: #2196f3;
}

.tab-manager >>> .v-tab--active {
  background: white;
  color: #2196f3;
  box-shadow: 0 -2px 8px rgba(33, 150, 243, 0.1);
  font-weight: 600;
}

/* Slider with gradient */
.tab-manager >>> .v-tabs-slider {
  height: 3px;
  background: linear-gradient(90deg, #00bcd4 0%, #2196f3 50%, #9c27b0 100%);
  border-radius: 2px 2px 0 0;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  min-height: 0;
  background: linear-gradient(180deg,
    rgba(250, 250, 250, 1) 0%,
    rgba(255, 255, 255, 1) 100%);
}

.tab-item {
  text-transform: none;
}

.tab-title {
  white-space: nowrap;
  overflow: visible;
}

.tab-input {
  border: 2px solid #2196f3;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 14px;
  min-width: 150px;
  background: white;
  transition: all 0.2s ease;
}

.tab-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.crud-tab-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Prilagodi sticky poziciju filtera unutar taba */
.tab-content >>> .table-header-filters {
  top: 0 !important;
}

/* Close button styling */
.tab-manager >>> .v-tab .v-btn {
  opacity: 0.6;
  transition: all 0.2s ease;
}

.tab-manager >>> .v-tab .v-btn:hover {
  opacity: 1;
  background: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

/* Overflow menu styling */
.tab-manager >>> .v-menu__content {
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 179, 190, 0.25);
  border: 2px solid rgba(0, 179, 190, 0.4);
  background: white !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 250 !important;
}

/* Overflow dropdown list */
.overflow-dropdown-list {
  padding: 8px 0 !important;
  background: white !important;
}

/* Overflow list items */
.overflow-list-item {
  padding: 12px 16px !important;
  min-height: 48px !important;
  transition: all 0.2s ease !important;
  border-radius: 12px !important;
  margin: 4px 8px !important;
}

.overflow-list-item:hover {
  background: rgba(0, 179, 190, 0.08) !important;
  transform: translateX(4px);
}

/* Overflow list title */
.overflow-list-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

/* Close button in overflow menu */
.close-tab-btn {
  opacity: 0.6;
  transition: all 0.2s ease !important;
}

.close-tab-btn:hover {
  opacity: 1;
  background: rgba(244, 67, 54, 0.1) !important;
  color: #f44336 !important;
  transform: scale(1.1);
}

.close-tab-btn .v-icon {
  color: rgba(244, 67, 54, 0.8) !important;
}

/* Badge styling */
.tab-manager >>> .v-badge__badge {
  font-size: 11px;
  font-weight: 600;
  background: linear-gradient(135deg, #ff5252 0%, #f44336 100%);
}
</style>
